import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { makeOwnerGate } from '../lib/ownerGate';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { dashboardsApi, requestsApi, exportApi, adminApi, profileApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  TrendingUp,
  Plus,
  ArrowRight,
  FlaskConical,
  FileCheck,
  Loader2,
  XCircle,
  ClipboardList,
  Activity,
  Users,
  UserCheck,
  UserX,
  Building2,
  Timer,
  Mail,
  RefreshCw,
  Wallet,
  Zap
} from 'lucide-react';
import { getStatusColor, getStatusLabel, getTypeColor, formatDate, getRoleLabel } from '../lib/utils';
import { toast } from 'sonner';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { EmailHealthBanner } from '../components/EmailHealthBanner';
import { BrandLoader } from '../components/BrandLoader';

// Clean, minimal stat card
function StatCard({ title, value, icon: Icon, variant = "default", subtitle, onClick, testId }) {
  // iter163o — Upgraded StatCard. Out: thin left-border + grey icon chip.
  // In: a semantic tinted icon chip, a soft top accent bar matching the
  // variant, larger numeric typography, and a subtler hover lift on
  // clickable tiles.
  const variants = {
    default: { accent: 'from-slate-300/60 to-transparent', chipBg: 'bg-slate-100', chipIcon: 'text-slate-500', ring: 'ring-slate-200/60' },
    primary: { accent: 'from-blue-500/70 to-transparent',  chipBg: 'bg-blue-50',  chipIcon: 'text-blue-600',  ring: 'ring-blue-200/60' },
    success: { accent: 'from-emerald-500/70 to-transparent', chipBg: 'bg-emerald-50', chipIcon: 'text-emerald-600', ring: 'ring-emerald-200/60' },
    warning: { accent: 'from-amber-500/70 to-transparent',   chipBg: 'bg-amber-50',   chipIcon: 'text-amber-600',   ring: 'ring-amber-200/60' },
    danger:  { accent: 'from-red-500/70 to-transparent',     chipBg: 'bg-red-50',     chipIcon: 'text-red-600',     ring: 'ring-red-200/60' },
    info:    { accent: 'from-cyan-500/70 to-transparent',    chipBg: 'bg-cyan-50',    chipIcon: 'text-cyan-600',    ring: 'ring-cyan-200/60' },
  };
  const v = variants[variant] || variants.default;

  return (
    <Card
      className={`relative overflow-hidden border border-slate-200/80 bg-white transition-all ${
        onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md' : ''
      }`}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Top accent bar */}
      <span aria-hidden className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${v.accent}`} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</p>
            <p className="text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${v.chipBg} ${v.ring}`}>
            <Icon className={`h-5 w-5 ${v.chipIcon}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Horizontal bar chart for status breakdown — iter164a polish:
// adds semantic icons, animated gradient bars, hover affordance, percentage chips.
function StatusBarChart({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Activity className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm">No request data yet</p>
        <p className="text-xs mt-1">Create your first request to see status breakdown</p>
      </div>
    );
  }

  const total = Object.values(data).reduce((sum, val) => sum + val, 0);
  const sortedEntries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const styleFor = (status) => {
    const s = (status || '').toUpperCase();
    if (s.includes('COMPLETED') || s.includes('SIGNED') || s.includes('EXECUTED')) {
      return { bar: 'from-emerald-400 to-emerald-500', dot: 'bg-emerald-500', icon: CheckCircle2, iconClass: 'text-emerald-600' };
    }
    if (s.includes('FAILED') || s.includes('REJECTED') || s.includes('WITHDRAWN')) {
      return { bar: 'from-red-400 to-red-500', dot: 'bg-red-500', icon: XCircle, iconClass: 'text-red-600' };
    }
    if (s.includes('PENDING') || s.includes('AWAITING') || s.includes('REVIEW') || s.includes('SIGNATURE')) {
      return { bar: 'from-amber-300 to-amber-500', dot: 'bg-amber-500', icon: Clock, iconClass: 'text-amber-600' };
    }
    if (s.includes('READY')) {
      return { bar: 'from-cyan-300 to-cyan-500', dot: 'bg-cyan-500', icon: Zap, iconClass: 'text-cyan-600' };
    }
    return { bar: 'from-blue-300 to-blue-500', dot: 'bg-blue-500', icon: Activity, iconClass: 'text-blue-600' };
  };

  return (
    <div className="space-y-3.5">
      {sortedEntries.map(([status, count]) => {
        const percentage = total > 0 ? (count / total * 100) : 0;
        const st = styleFor(status);
        const StatusIcon = st.icon;
        return (
          <div key={status} className="group">
            <div className="flex items-center justify-between mb-1.5 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <StatusIcon className={`w-3.5 h-3.5 ${st.iconClass} shrink-0`} />
                <span className="text-[13px] text-slate-600 group-hover:text-slate-900 transition-colors truncate">
                  {getStatusLabel(status)}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] font-medium text-slate-400 tabular-nums">{percentage.toFixed(0)}%</span>
                <span className="text-sm font-semibold text-slate-900 tabular-nums">{count}</span>
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden relative">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${st.bar} transition-all duration-1000 ease-out group-hover:brightness-110`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
      <p className="text-xs text-slate-400 text-center pt-3 border-t border-slate-100">
        <span className="font-medium text-slate-600 tabular-nums">{total}</span> total requests
      </p>
    </div>
  );
}

// Circular progress for completion rate — iter164a polish: gradient stroke,
// animated count-up, larger ring, contextual subtitle (X of Y done).
function CompletionRing({ percentage, total, done }) {
  const [displayPct, setDisplayPct] = useState(0);
  const safePct = Math.max(0, Math.min(100, Math.round(percentage || 0)));

  useEffect(() => {
    let raf;
    const start = performance.now();
    const duration = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // ease-out
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayPct(Math.round(safePct * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [safePct]);

  const radius = 52;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safePct / 100) * circumference;
  const size = 140;
  const center = size / 2;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${size} ${size}`}>
          <defs>
            <linearGradient id="completionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="55%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
          </defs>
          <circle
            cx={center} cy={center} r={radius}
            fill="none"
            stroke="#f1f5f9"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center} cy={center} r={radius}
            fill="none"
            stroke="url(#completionGradient)"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent tabular-nums">
            {displayPct}%
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Complete</span>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600">Task Completion Rate</p>
      {total != null && done != null && (
        <p className="text-xs text-slate-400 mt-0.5 tabular-nums">{done} of {total} done</p>
      )}
    </div>
  );
}

// Role colors for pie chart
const ROLE_COLORS = {
  'ADMIN': '#64748b',
  'CRD': '#8b5cf6',
  'CHAIRMAN': '#f59e0b',
  'COORDINATOR': '#10b981',
  'DIRECTOR': '#ef4444',
  'EXEC_DIRECTOR': '#ec4899',
  'INVESTIGATOR': '#3b82f6',
  'PI': '#14b8a6',
  'CONSULTANT': '#6366f1',
  'REVIEWER': '#f97316',
  'COMPANY_REP': '#84cc16',
};

// Users by Role Pie Chart — iter164a polish: larger donut, total in center,
// modernized legend with color chips, percentages, and a hover affordance.
function UsersByRolePieChart({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-slate-400">
        <Users className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm">No user data</p>
      </div>
    );
  }

  const chartData = Object.entries(data).map(([role, count]) => ({
    name: getRoleLabel(role),
    value: count,
    role: role
  })).sort((a, b) => b.value - a.value);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-44 h-44 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={78}
              paddingAngle={2.5}
              dataKey="value"
              stroke="#ffffff"
              strokeWidth={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={ROLE_COLORS[entry.role] || '#94a3b8'} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${value} (${((value / total) * 100).toFixed(0)}%)`, name]}
              contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-slate-900 tabular-nums">{total}</span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Users</span>
        </div>
      </div>
      <div className="flex-1 space-y-1.5 min-w-0">
        {chartData.slice(0, 6).map((item) => {
          const pct = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          const color = ROLE_COLORS[item.role] || '#94a3b8';
          return (
            <div
              key={item.role}
              className="flex items-center gap-2 text-xs group hover:bg-slate-50 rounded-md px-1.5 py-1 -mx-1.5 transition-colors"
            >
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0 ring-2 ring-white shadow-sm"
                style={{ backgroundColor: color }}
              />
              <span className="text-slate-700 group-hover:text-slate-900 truncate flex-1 text-[12.5px]">{item.name}</span>
              <span className="text-[10px] font-medium text-slate-400 tabular-nums w-8 text-right">{pct}%</span>
              <span className="font-semibold text-slate-900 tabular-nums w-6 text-right">{item.value}</span>
            </div>
          );
        })}
        {chartData.length > 6 && (
          <p className="text-[10px] text-slate-400 pl-1.5 pt-1">+{chartData.length - 6} more roles</p>
        )}
      </div>
    </div>
  );
}

// iter164a — Compact list card used on the Admin/Chairman/CRD/Director/Exec
// Director dashboard to surface the top "Pending Actions" and "Overdue" items
// in place of the old generic Quick Actions panel. Each row navigates to the
// request and the footer button opens the existing detail modal.
function ActionListCard({ title, icon: Icon, accent = 'amber', loading, data, emptyLabel, onViewAll, onItemClick, variant, testId }) {
  const accents = {
    amber: { ring: 'ring-amber-100', bg: 'bg-amber-50', icon: 'text-amber-600', btn: 'text-amber-700 hover:text-amber-800 hover:bg-amber-50', bar: 'from-amber-400/80' },
    red:   { ring: 'ring-red-100',   bg: 'bg-red-50',   icon: 'text-red-600',   btn: 'text-red-700 hover:text-red-800 hover:bg-red-50',     bar: 'from-red-500/80' },
  };
  const a = accents[accent] || accents.amber;
  const items = data?.items || [];
  const total = data?.total || 0;
  const topItems = items.slice(0, 5);

  const fmtRelativeDue = (item) => {
    if (variant === 'overdue') {
      if (item.days_overdue >= 1) return `${item.days_overdue}d overdue`;
      return 'Overdue';
    }
    if (!item.due_date) return 'No due date';
    if (item.is_overdue) return `${item.days_overdue}d overdue`;
    try {
      const due = new Date(item.due_date);
      const now = new Date();
      const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
      if (diff <= 0) return 'Due today';
      if (diff === 1) return 'Due tomorrow';
      return `Due in ${diff}d`;
    } catch (e) {
      return 'Pending';
    }
  };

  // iter164b — Per-role hover styling for the small role badge.
  const roleBadgeClass = (role) => {
    const r = (role || '').toUpperCase();
    const map = {
      COORDINATOR:   'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:text-emerald-800',
      INVESTIGATOR:  'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:text-blue-800',
      PI:            'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100 hover:border-teal-300 hover:text-teal-800',
      REVIEWER:      'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300 hover:text-orange-800',
      CHAIRMAN:      'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 hover:text-amber-800',
      DIRECTOR:      'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:border-rose-300 hover:text-rose-800',
      EXEC_DIRECTOR: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100 hover:border-pink-300 hover:text-pink-800',
      FINANCE:       'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 hover:border-violet-300 hover:text-violet-800',
      CONSULTANT:    'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300 hover:text-indigo-800',
      COMPANY_REP:   'bg-lime-50 text-lime-700 border-lime-200 hover:bg-lime-100 hover:border-lime-300 hover:text-lime-800',
    };
    return map[r] || 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 hover:border-slate-300';
  };

  return (
    <Card className="relative overflow-hidden border border-slate-200/80" data-testid={testId}>
      <span aria-hidden className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${a.bar} to-transparent`} />
      <CardHeader className="pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ring-1 ${a.bg} ${a.ring}`}>
              <Icon className={`w-4 h-4 ${a.icon}`} />
            </span>
            {title}
            <Badge variant="outline" className="ml-1 text-[10px] font-medium text-slate-600">
              {total}
            </Badge>
          </CardTitle>
          {total > 5 && (
            <Button
              variant="ghost"
              size="sm"
              className={`text-xs ${a.btn}`}
              onClick={onViewAll}
              data-testid={`${testId}-view-all`}
            >
              View all <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="py-10 flex items-center justify-center">
            <BrandLoader size="sm" />
          </div>
        ) : topItems.length === 0 ? (
          <div className="py-10 text-center">
            <CheckCircle2 className="w-9 h-9 mx-auto text-emerald-500 mb-2" />
            <p className="text-sm text-slate-500">{emptyLabel}</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {topItems.map((it) => (
              <li
                key={it.assignment_id}
                role="button"
                tabIndex={0}
                onClick={() => onItemClick?.(it)}
                onKeyDown={(e) => { if (e.key === 'Enter') onItemClick?.(it); }}
                className="group flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid={`${testId}-row-${it.assignment_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-blue-600 group-hover:underline">{it.ref_no}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] capitalize px-1.5 py-0 border transition-colors ${roleBadgeClass(it.assignment_role)}`}
                    >
                      {(it.assignment_role || '').toLowerCase().replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium text-slate-800 truncate mt-0.5" title={it.request_title}>
                    {it.request_title || 'Untitled request'}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {variant === 'overdue' ? (
                      <>To: <span className="text-slate-700 font-medium">{it.assignee_name}</span></>
                    ) : (
                      <>{it.action_label || 'Take action'} · <span className="text-slate-700 font-medium">{it.assignee_name}</span></>
                    )}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    it.is_overdue
                      ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
                      : 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                  }`}>
                    {it.is_overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    {fmtRelativeDue(it)}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {total > 0 && (
        <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50/40">
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewAll}
            className={`w-full text-xs justify-center ${a.btn}`}
            data-testid={`${testId}-view-all-footer`}
          >
            View all {total > 0 ? `(${total})` : ''} <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { user, isCompanyRep, isCRD, isAdmin, isConsultant, isChairman, isCoordinator, isDirector, isExecDirector, isReviewer, isCompanyOwner } = useAuth();
  const isFinance = () => user?.role === 'FINANCE';
  const blockIfOwner = makeOwnerGate(isCompanyOwner);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [overdueBreakdown, setOverdueBreakdown] = useState(null);
  const [overdueDetails, setOverdueDetails] = useState(null);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [overdueMode, setOverdueMode] = useState('all'); // 'all' (admin) or 'mine' (personal)
  const [sendingReminderFor, setSendingReminderFor] = useState(null); // assignment_id being processed
  const [sendingAllReminders, setSendingAllReminders] = useState(false);
  const [overdueRoleFilter, setOverdueRoleFilter] = useState('ALL');
  const [overdueSearch, setOverdueSearch] = useState('');
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [expiryReport, setExpiryReport] = useState(null);

  // iter164a — Inline dashboard list previews for Admin/Chairman/CRD/Director/
  // ExecDirector. These power the two new "Pending Actions" + "Overdue" list
  // cards that replaced the Quick Actions panel.
  const [pendingActionsList, setPendingActionsList] = useState(null); // {total, items}
  const [overdueList, setOverdueList] = useState(null); // {total, items}
  const [listsLoading, setListsLoading] = useState(false);


  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchExpiryAlerts();
  }, []);

  const fetchExpiryAlerts = async () => {
    try {
      const res = await profileApi.getExpiryAlerts();
      setExpiryAlerts(res.data?.alerts || []);
      setMissingDocs(res.data?.missing_docs || []);
    } catch (e) {}
  };

  const fetchDashboardData = async () => {
    try {
      if (isCRD() || isAdmin() || isChairman() || isDirector() || isExecDirector() || isFinance()) {
        const statsRes = await dashboardsApi.getAdmin();
        setStats(statsRes.data);
        
        // iter164a — Pull top items for the new Pending Actions / Overdue list
        // cards. Both use the existing detail endpoints; we just slice client-side.
        setListsLoading(true);
        try {
          const [pendingRes, overdueRes] = await Promise.all([
            dashboardsApi.getAllPendingActions().catch(() => ({ data: { total: 0, items: [] } })),
            dashboardsApi.getOverdueDetails().catch(() => ({ data: { total: 0, items: [] } })),
          ]);
          setPendingActionsList(pendingRes.data);
          setOverdueList(overdueRes.data);
        } catch (e) {
          // silent — list panels degrade gracefully to empty state
        } finally {
          setListsLoading(false);
        }

        // Fetch user management stats and scheduler status for admin
        if (isAdmin()) {
          try {
            const [userStatsRes, schedulerRes, expiryRes] = await Promise.all([
              dashboardsApi.getUserManagementStats(),
              adminApi.getSchedulerStatus(),
              profileApi.getExpiryReport().catch(() => ({ data: null }))
            ]);
            setUserStats(userStatsRes.data);
            setSchedulerStatus(schedulerRes.data);
            if (expiryRes.data) setExpiryReport(expiryRes.data);
          } catch (e) {
            console.log('Admin stats not available');
          }
        }
      } else if (isCoordinator()) {
        const statsRes = await dashboardsApi.getCoordinator();
        setStats({ ...statsRes.data, role_type: 'coordinator' });
      } else if (isConsultant()) {
        const statsRes = await dashboardsApi.getConsultant();
        setStats({ ...statsRes.data, role_type: 'investigator' });
      } else if (isReviewer()) {
        try {
          const statsRes = await dashboardsApi.getReviewer();
          setStats({ ...statsRes.data, role_type: 'reviewer' });
        } catch (e) {
          setStats({ total_assigned: 0, completed: 0, pending: 0, completion_rate: 0, my_overdue_items: 0, role_type: 'reviewer' });
        }
      } else if (isCompanyRep()) {
        const requestsRes = await requestsApi.getAll();
        const myRequests = requestsRes.data;
        // iter163f — Include EXECUTED + READY_TO_EXECUTE in the Company Rep
        // dashboard's `completed` bucket. Modern workflows (CTA, FT) finish at
        // EXECUTED, not at the legacy COMPLETED / SIGNED statuses.
        const finalStatuses = ['COMPLETED', 'SIGNED', 'EXECUTED', 'READY_TO_EXECUTE'];
        const terminalStatuses = [...finalStatuses, 'FAILED', 'REJECTED', 'WITHDRAWN'];
        const pending = myRequests.filter(r => !terminalStatuses.includes(r.status)).length;
        const completed = myRequests.filter(r => finalStatuses.includes(r.status)).length;
        setStats({
          my_requests: myRequests.length,
          pending: pending,
          completed: completed,
          role_type: 'company'
        });
      }

      const requestsRes = await requestsApi.getAll();
      // iter163h — Strip is_locked (restricted) requests from the Recent Activity
      // panel for Company / Company Rep accounts, matching the RequestsList behaviour.
      // Sponsors should never see "Restricted — not visible to your account" leaks.
      let visibleRequests = (isCompanyRep() || isCompanyOwner())
        ? requestsRes.data.filter(r => !r.is_locked)
        : requestsRes.data;
      // iter167 Issue 26 — FINANCE dashboard is FT-only. Even though /requests
      // legitimately surfaces non-FT requests where the FINANCE user is a
      // review-stage reviewer, the dashboard's Recent Activity widget must
      // filter those out so the FINANCE Overview stays a pure FT view (parity
      // with the FT-only KPI tiles).
      if (isFinance()) {
        visibleRequests = visibleRequests.filter(r => r.type === 'FT');
      }
      setRecentRequests(visibleRequests.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOverdueClick = async () => {
    setOverdueMode('all');
    setShowOverdueModal(true);
    setOverdueLoading(true);
    try {
      const res = await dashboardsApi.getOverdueDetails();
      setOverdueDetails(res.data);
      setOverdueBreakdown({ total: res.data.total, by_role: res.data.by_role });
    } catch (error) {
      toast.error('Failed to fetch overdue details');
    } finally {
      setOverdueLoading(false);
    }
  };

  const handleMyOverdueClick = async () => {
    setOverdueMode('mine');
    setShowOverdueModal(true);
    setOverdueLoading(true);
    try {
      const res = await dashboardsApi.getMyOverdueDetails();
      setOverdueDetails(res.data);
      setOverdueBreakdown({ total: res.data.total, by_role: res.data.by_role });
    } catch (error) {
      toast.error('Failed to fetch your overdue items');
    } finally {
      setOverdueLoading(false);
    }
  };

  const handleMyPendingActionsClick = async () => {
    setOverdueMode('pending');
    setShowOverdueModal(true);
    setOverdueLoading(true);
    try {
      const res = await dashboardsApi.getMyPendingActions();
      setOverdueDetails(res.data);
      setOverdueBreakdown({ total: res.data.total, by_role: res.data.by_role });
    } catch (error) {
      toast.error('Failed to fetch your pending actions');
    } finally {
      setOverdueLoading(false);
    }
  };

  // iter164a — Admin-level "Pending Actions" modal — shows EVERY user's pending
  // assignments (not just mine) for Admin/Chairman/CRD/Director/Exec Director.
  const handleAllPendingActionsClick = async () => {
    setOverdueMode('all-pending');
    setShowOverdueModal(true);
    setOverdueLoading(true);
    try {
      const res = await dashboardsApi.getAllPendingActions();
      setOverdueDetails(res.data);
      setOverdueBreakdown({ total: res.data.total, by_role: res.data.by_role });
    } catch (error) {
      toast.error('Failed to fetch pending actions');
    } finally {
      setOverdueLoading(false);
    }
  };

  const refreshOverdueDetails = async () => {
    setOverdueLoading(true);
    try {
      let res;
      if (overdueMode === 'pending') {
        res = await dashboardsApi.getMyPendingActions();
      } else if (overdueMode === 'all-pending') {
        res = await dashboardsApi.getAllPendingActions();
      } else if (overdueMode === 'mine') {
        res = await dashboardsApi.getMyOverdueDetails();
      } else {
        res = await dashboardsApi.getOverdueDetails();
      }
      setOverdueDetails(res.data);
      setOverdueBreakdown({ total: res.data.total, by_role: res.data.by_role });
    } catch (e) {
      // silent
    } finally {
      setOverdueLoading(false);
    }
  };

  const handleSendSingleReminder = async (assignmentId) => {
    setSendingReminderFor(assignmentId);
    try {
      const res = await dashboardsApi.sendOverdueReminder(assignmentId);
      if (res.data?.delivered) {
        toast.success('Urgent reminder email sent');
      } else {
        toast.warning('Reminder logged but email delivery may have failed');
      }
      await refreshOverdueDetails();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error(detail || 'Reminder was already sent recently (24h cooldown).');
      } else {
        toast.error(detail || 'Failed to send reminder');
      }
    } finally {
      setSendingReminderFor(null);
    }
  };

  const handleSendAllReminders = async () => {
    if (!window.confirm('Send urgent reminder emails to every eligible overdue assignee?')) return;
    setSendingAllReminders(true);
    try {
      const res = await dashboardsApi.sendAllOverdueReminders();
      const { sent, skipped } = res.data || {};
      if (sent > 0) {
        toast.success(`Sent ${sent} reminder${sent === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}.`);
      } else {
        toast.info(`No reminders sent (${skipped || 0} skipped — likely all within 24h cooldown).`);
      }
      await refreshOverdueDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk reminders');
    } finally {
      setSendingAllReminders(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await exportApi.requestsCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clinicalflow_requests_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      await adminApi.triggerReminders();
      toast.success('Reminder check triggered successfully');
      // Refresh scheduler status
      const schedulerRes = await adminApi.getSchedulerStatus();
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      toast.error('Failed to trigger reminders');
    } finally {
      setTriggeringReminders(false);
    }
  };

  const refreshSchedulerStatus = async () => {
    try {
      const schedulerRes = await adminApi.getSchedulerStatus();
      setSchedulerStatus(schedulerRes.data);
      toast.success('Status refreshed');
    } catch (error) {
      toast.error('Failed to refresh status');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <BrandLoader size="lg" label="Loading your dashboard…" />
        </div>
      </DashboardLayout>
    );
  }

  // Admin/Management Dashboard
  const renderAdminDashboard = () => (
    <div className="space-y-6">
      <EmailHealthBanner />
      {/* Summary Stats — iter164a: replaced "Overdue" with "Failed" in the
          primary row per product direction. Overdue moved into a dedicated list
          card below alongside Pending Actions.
          iter167 Issue 26 — for FINANCE viewers the four primary tiles are
          relabelled to "FT REQUESTS / IN PROGRESS / COMPLETED / FAILED" and
          the secondary FEA/CTA/FT/My-Actions row is suppressed (they only
          manage FT and shouldn't see other-type counters). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={isFinance() ? "FT Requests" : "Total Requests"}
          value={stats?.total_requests || 0}
          icon={FileText}
          variant="primary"
          testId="kpi-total-requests"
        />
        <StatCard
          title="In Progress"
          value={stats?.pending_requests || 0}
          icon={Clock}
          variant="warning"
          testId="kpi-pending"
        />
        <StatCard
          title="Completed"
          value={stats?.completed_requests || 0}
          icon={CheckCircle2}
          variant="success"
          testId="kpi-completed"
        />
        <StatCard
          title="Failed"
          value={stats?.failed_requests || 0}
          icon={XCircle}
          variant={stats?.failed_requests > 0 ? "danger" : "default"}
          testId="kpi-failed"
        />
      </div>

      {/* Secondary Stats — iter164a: dropped the duplicate "Failed" tile (now
          promoted into the primary row) and kept the request-type tiles plus the
          personal "My Actions" tile.
          iter167 Issue 26 — hidden entirely for FINANCE (their primary row is
          already FT-scoped so the type breakdown adds no value). */}
      {!isFinance() && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="FEA Requests"
          value={stats?.fea_requests || 0}
          icon={FlaskConical}
          variant="info"
          testId="kpi-fea-requests"
        />
        <StatCard
          title="CTA Requests"
          value={stats?.cta_requests || 0}
          icon={FileCheck}
          variant="info"
          testId="kpi-cta-requests"
        />
        <StatCard
          title="FT Requests"
          value={stats?.ft_requests || 0}
          icon={Wallet}
          variant="info"
          testId="kpi-ft-requests"
        />
        <StatCard
          title="My Actions"
          value={stats?.my_pending_actions || 0}
          icon={ClipboardList}
          variant="primary"
          subtitle={stats?.my_pending_actions > 0 ? "Click to view yours" : null}
          onClick={stats?.my_pending_actions > 0 ? handleMyPendingActionsClick : undefined}
          testId="kpi-my-pending"
        />
      </div>
      )}

      {/* User Stats Section (Admin only) */}
      {isAdmin() && userStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={userStats.total_users}
            icon={Users}
            variant="default"
            testId="kpi-total-users"
          />
          <StatCard
            title="Active Users"
            value={userStats.active_users}
            icon={UserCheck}
            variant="success"
            testId="kpi-active-users"
          />
          <StatCard
            title="Inactive Users"
            value={userStats.inactive_users}
            icon={UserX}
            variant="default"
            testId="kpi-inactive-users"
          />
          <StatCard
            title="Companies"
            value={userStats.total_companies}
            icon={Building2}
            variant="info"
            testId="kpi-total-companies"
          />
        </div>
      )}

      {/* iter164b — Pending Actions + Overdue list cards are now rendered
          AFTER the Recent Activity section at the page level. This block was
          removed from renderAdminDashboard per product direction. */}

      {/* Charts Section — Requests by Status + Users by Role */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200/80">
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
              <Activity className="w-4 h-4 text-blue-600" />
              Requests by Status
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <StatusBarChart data={stats?.requests_by_status} />
          </CardContent>
        </Card>

        {isAdmin() && userStats ? (
          <Card className="border-slate-200/80">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <Users className="w-4 h-4 text-violet-600" />
                Users by Role
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <UsersByRolePieChart data={userStats.users_by_role} />
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200/80">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <Zap className="w-4 h-4 text-amber-500" />
                At a Glance
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              <div className="flex items-center justify-around">
                <CompletionRing
                  percentage={stats?.completed_requests && stats?.total_requests
                    ? Math.round((stats.completed_requests / stats.total_requests) * 100)
                    : 0}
                  total={stats?.total_requests || 0}
                  done={stats?.completed_requests || 0}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* iter163m — Reminder Scheduler block removed per user request.
          The scheduler still runs in the background; surface it on the
          Admin → Configuration page if visibility is needed. */}
    </div>
  );

  // Coordinator/Investigator/Reviewer Dashboard
  const renderAssignmentDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats Column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              title="Total Assigned"
              value={stats?.total_assigned || 0}
              icon={FileText}
              variant="primary"
              testId="kpi-total-assigned"
            />
            <StatCard
              title="Completed"
              value={stats?.completed || 0}
              icon={CheckCircle2}
              variant="success"
              testId="kpi-completed"
            />
            <StatCard
              title="Pending Actions"
              value={stats?.pending || 0}
              icon={Clock}
              variant="warning"
              subtitle={stats?.pending > 0 ? "Click for details" : null}
              onClick={stats?.pending > 0 ? handleMyPendingActionsClick : undefined}
              testId="kpi-pending"
            />
            <StatCard
              title="Overdue"
              value={stats?.my_overdue_items || 0}
              icon={AlertTriangle}
              variant={stats?.my_overdue_items > 0 ? "danger" : "default"}
              subtitle={stats?.my_overdue_items > 0 ? "Click for details" : null}
              onClick={stats?.my_overdue_items > 0 ? handleMyOverdueClick : undefined}
              testId="kpi-overdue"
            />
          </div>
        </div>

        {/* Completion Rate */}
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <CompletionRing
              percentage={stats?.completion_rate || 0}
              total={stats?.total_assigned || 0}
              done={stats?.completed || 0}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Company Rep Dashboard
  const renderCompanyDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="My Requests"
          value={stats?.my_requests || 0}
          icon={FileText}
          variant="primary"
          testId="kpi-my-requests"
        />
        <StatCard
          title="In Progress"
          value={stats?.pending || 0}
          icon={Clock}
          variant="warning"
          testId="kpi-pending"
        />
        <StatCard
          title="Completed"
          value={stats?.completed || 0}
          icon={CheckCircle2}
          variant="success"
          testId="kpi-completed"
        />
      </div>

      <Card>
        <CardContent className="p-6 text-center">
          <Plus className="w-12 h-12 text-blue-600 mx-auto mb-3" />
          <h3 className="font-medium text-slate-900 mb-1">Start a New Study</h3>
          <p className="text-sm text-slate-500 mb-4">Create a study to group your feasibility and clinical trial requests</p>
          <Button onClick={() => { if (blockIfOwner()) return; navigate('/studies/new'); }} className="bg-blue-600 hover:bg-blue-700" data-testid="dash-create-study-cta">
            <Plus className="w-4 h-4 mr-2" />
            Create Study
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Determine which dashboard to render
  const getDashboardContent = () => {
    if (isCRD() || isAdmin() || isChairman() || isDirector() || isExecDirector() || isFinance()) {
      return renderAdminDashboard();
    } else if (isCoordinator() || isConsultant() || isReviewer() || stats?.role_type === 'coordinator' || stats?.role_type === 'investigator' || stats?.role_type === 'reviewer') {
      return renderAssignmentDashboard();
    } else if (isCompanyRep() || stats?.role_type === 'company') {
      return renderCompanyDashboard();
    }
    return renderAdminDashboard();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* iter163o — Polished header with greeting + subtle gradient title and
            a small "live" pulse to signal the dashboard is real-time. */}
        <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-blue-50/40 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-2xl font-semibold tracking-tight text-transparent sm:text-3xl">
              Welcome back, {user?.name || 'User'}
            </h1>
            <div className="mt-1.5 flex items-center gap-2 text-sm text-slate-500">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Here&apos;s what&apos;s happening today
            </div>
          </div>
          {isCompanyOwner() ? (
            // iter166h — Company owners can't create requests (only their
            // reps can). Replace the request-creation CTA with a shiny
            // emerald "Invite your Representative" deep-link to
            // /company/reps?invite=1 — the CompanyReps page reads the
            // query string and pops the Invite modal immediately
            // (iter167 Bug 5).
            <Button
              onClick={() => navigate('/company/reps?invite=1')}
              className="group shrink-0 relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/30 ring-1 ring-emerald-300/40 transition-all hover:shadow-emerald-500/50 hover:scale-[1.02]"
              data-testid="add-company-rep-btn"
            >
              <span className="absolute inset-0 rounded-md bg-gradient-to-br from-white/15 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
              <Plus className="w-4 h-4 mr-2 relative" />
              <span className="relative font-semibold">Invite your Representative</span>
            </Button>
          ) : (isCompanyRep() || isCoordinator()) && (
            <Button onClick={() => { if (blockIfOwner()) return; navigate('/requests/new'); }} className="shrink-0 bg-blue-600 hover:bg-blue-700" data-testid="add-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Request
            </Button>
          )}
        </div>

        {/* Missing Required Documents Banner */}
        {missingDocs.length > 0 && (
          <Card className="border-red-200 bg-red-50/50 mb-4" data-testid="missing-docs-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 text-sm">Required Documents Missing</h3>
                  <p className="text-xs text-red-700 mt-1">
                    You have {missingDocs.length} required document{missingDocs.length !== 1 ? 's' : ''} to upload before you can be assigned to requests:
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {missingDocs.map((doc, i) => (
                      <Badge key={i} className={doc.enforce === 'block_assign' ? 'bg-red-100 text-red-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                        {doc.doc_type}{doc.request_type ? ` (${doc.request_type})` : ''}
                      </Badge>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3 text-xs border-red-300 text-red-700 hover:bg-red-100" onClick={() => navigate('/profile')}>
                    Go to Profile to Upload
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Expiry Alerts */}
        {expiryAlerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50 mb-4" data-testid="doc-expiry-alerts">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 text-sm mb-2">Document Alerts</h3>
                  <div className="space-y-1.5">
                    {expiryAlerts.slice(0, 8).map((alert, i) => (
                      <div key={i} className={`text-xs flex items-center gap-1.5 ${alert.expired ? 'text-red-700' : 'text-amber-700'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${alert.expired ? 'bg-red-500' : 'bg-amber-500'}`} />
                        {alert.type === 'own_doc' ? (
                          <span>Your <strong>{alert.doc_type}</strong> {alert.expired ? 'has expired' : `expires in ${alert.days_until} days`}</span>
                        ) : alert.type === 'assigned_user_missing_doc' ? (
                          <span>
                            <strong>{alert.user_name}</strong> is missing <strong>{alert.doc_type}</strong>
                            {alert.request_ref && <span className="text-slate-500 ml-1">({alert.request_ref})</span>}
                          </span>
                        ) : (
                          <span>
                            <strong>{alert.user_name}</strong>'s {alert.doc_type} {alert.expired ? 'has expired' : `expires in ${alert.days_until} days`}
                            {alert.request_ref && <span className="text-slate-500 ml-1">({alert.request_ref})</span>}
                          </span>
                        )}
                      </div>
                    ))}
                    {expiryAlerts.length > 8 && (
                      <p className="text-[10px] text-amber-600">+ {expiryAlerts.length - 8} more alerts</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Role-specific dashboard content */}
        {getDashboardContent()}

        {/* Recent Requests */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-slate-700">Recent Activity</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/requests')} className="text-blue-600 hover:text-blue-700">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentRequests.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No recent requests</p>
                <p className="text-slate-400 text-xs mt-1">Your recent activity will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentRequests.map(request => (
                  <div 
                    key={request.id}
                    className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 -mx-2 rounded cursor-pointer transition-colors"
                    onClick={() => navigate(`/requests/${request.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={`text-xs ${getTypeColor(request.type)}`}>
                        {request.type}
                      </Badge>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{request.ref_no}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px] sm:max-w-xs">{request.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status, request.type)}
                      </Badge>
                      <span className="text-xs text-slate-400 hidden sm:block">{formatDate(request.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter164b — Admin/Chairman/CRD/Director/ExecDirector: Pending Actions
            and Overdue list cards moved BELOW Recent Activity per product
            direction. Hidden for non-admin roles since those dashboards don't
            even render this section. */}
        {(isCRD() || isAdmin() || isChairman() || isDirector() || isExecDirector()) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ActionListCard
              title="Pending Actions"
              icon={ClipboardList}
              accent="amber"
              loading={listsLoading}
              data={pendingActionsList}
              emptyLabel="No pending actions across the team 🎉"
              onViewAll={handleAllPendingActionsClick}
              onItemClick={(it) => navigate(`/requests/${it.request_id}`)}
              variant="pending"
              testId="pending-actions-list"
            />
            <ActionListCard
              title="Overdue"
              icon={AlertTriangle}
              accent="red"
              loading={listsLoading}
              data={overdueList}
              emptyLabel="Nothing overdue 🎉"
              onViewAll={handleOverdueClick}
              onItemClick={(it) => navigate(`/requests/${it.request_id}`)}
              variant="overdue"
              testId="overdue-list"
            />
          </div>
        )}
      </div>

      {/* Overdue Breakdown Modal */}
      <Dialog open={showOverdueModal} onOpenChange={setShowOverdueModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="overdue-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(overdueMode === 'pending' || overdueMode === 'all-pending') ? (
                <Clock className="w-5 h-5 text-amber-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              )}
              {overdueMode === 'pending'
                ? 'My Pending Actions'
                : overdueMode === 'all-pending'
                  ? 'All Pending Actions'
                  : overdueMode === 'mine'
                    ? 'My Overdue Items'
                    : 'Overdue Items'}
              {overdueDetails && (
                <span className="text-sm font-normal text-slate-500">
                  ({overdueDetails.total} total)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Top summary + bulk action */}
          <div className="flex flex-col gap-4">
            <div className="flex items-stretch gap-3 flex-wrap">
              <div className="px-5 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertTriangle className="w-7 h-7 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-red-600 leading-none" data-testid="overdue-total">
                    {overdueDetails?.total ?? overdueBreakdown?.total ?? 0}
                  </p>
                  <p className="text-xs text-red-600/70 mt-1">Total overdue</p>
                </div>
              </div>
              {Object.entries(overdueDetails?.by_role || overdueBreakdown?.by_role || {}).map(([role, count]) => {
                // iter164b — Per-role chip color so hover/active states feel
                // semantic rather than a flat slate ramp. Active state uses the
                // same hue at higher saturation; hover lights the matching tint.
                const chipPalette = {
                  COORDINATOR: { idle: 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300', active: 'bg-emerald-600 border-emerald-600 text-white shadow-md shadow-emerald-200' },
                  INVESTIGATOR: { idle: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100 hover:border-blue-300', active: 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200' },
                  PI:           { idle: 'bg-teal-50 border-teal-200 text-teal-800 hover:bg-teal-100 hover:border-teal-300', active: 'bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-200' },
                  REVIEWER:     { idle: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100 hover:border-orange-300', active: 'bg-orange-600 border-orange-600 text-white shadow-md shadow-orange-200' },
                  CHAIRMAN:     { idle: 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 hover:border-amber-300', active: 'bg-amber-600 border-amber-600 text-white shadow-md shadow-amber-200' },
                  DIRECTOR:     { idle: 'bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100 hover:border-rose-300', active: 'bg-rose-600 border-rose-600 text-white shadow-md shadow-rose-200' },
                  EXEC_DIRECTOR:{ idle: 'bg-pink-50 border-pink-200 text-pink-800 hover:bg-pink-100 hover:border-pink-300', active: 'bg-pink-600 border-pink-600 text-white shadow-md shadow-pink-200' },
                  FINANCE:      { idle: 'bg-violet-50 border-violet-200 text-violet-800 hover:bg-violet-100 hover:border-violet-300', active: 'bg-violet-600 border-violet-600 text-white shadow-md shadow-violet-200' },
                  CONSULTANT:   { idle: 'bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300', active: 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-200' },
                  COMPANY_REP:  { idle: 'bg-lime-50 border-lime-200 text-lime-800 hover:bg-lime-100 hover:border-lime-300', active: 'bg-lime-600 border-lime-600 text-white shadow-md shadow-lime-200' },
                };
                const isActive = overdueRoleFilter === role;
                const p = chipPalette[role] || { idle: 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300', active: 'bg-slate-900 border-slate-900 text-white shadow-md' };
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setOverdueRoleFilter(overdueRoleFilter === role ? 'ALL' : role)}
                    className={`px-4 py-3 rounded-lg border text-left transition-all duration-200 hover:-translate-y-0.5 ${isActive ? p.active : p.idle}`}
                    data-testid={`overdue-role-chip-${role}`}
                  >
                    <p className="text-xl font-bold leading-none">{count}</p>
                    <p className="text-xs mt-1 capitalize">{role.toLowerCase().replace(/_/g, ' ')}</p>
                  </button>
                );
              })}
              {overdueMode === 'all' && (isAdmin() || isCRD() || isChairman()) && (overdueDetails?.items?.length || 0) > 0 && (
                <div className="ml-auto flex items-center">
                  <Button
                    onClick={handleSendAllReminders}
                    disabled={sendingAllReminders}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="overdue-send-all-btn"
                  >
                    {sendingAllReminders ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Send all urgent reminders</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                placeholder="Search by request ref, title, or assignee name…"
                value={overdueSearch}
                onChange={(e) => setOverdueSearch(e.target.value)}
                className="flex-1 min-w-[200px] h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-200"
                data-testid="overdue-search-input"
              />
              {overdueRoleFilter !== 'ALL' && (
                <Button variant="outline" size="sm" onClick={() => setOverdueRoleFilter('ALL')} data-testid="overdue-clear-filter-btn">
                  <XCircle className="w-3 h-3 mr-1" /> Clear filter
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={refreshOverdueDetails} disabled={overdueLoading} data-testid="overdue-refresh-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${overdueLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Items list */}
          <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2">
            {overdueLoading && !overdueDetails ? (
              <div className="py-10 flex items-center justify-center">
                <BrandLoader size="sm" label="Loading…" />
              </div>
            ) : !overdueDetails || overdueDetails.items.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                <p>{overdueMode === 'pending' || overdueMode === 'all-pending' ? 'No pending actions 🎉' : 'No overdue items 🎉'}</p>
              </div>
            ) : (
              (() => {
                const q = overdueSearch.trim().toLowerCase();
                const filtered = overdueDetails.items.filter(it => {
                  if (overdueRoleFilter !== 'ALL' && it.assignment_role !== overdueRoleFilter) return false;
                  if (!q) return true;
                  return (
                    it.ref_no?.toLowerCase().includes(q) ||
                    it.request_title?.toLowerCase().includes(q) ||
                    it.assignee_name?.toLowerCase().includes(q) ||
                    it.assignee_email?.toLowerCase().includes(q)
                  );
                });
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-10 text-slate-500 text-sm">
                      No items match the current filter / search.
                    </div>
                  );
                }
                return (
                  <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="overdue-items-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr className="text-left text-xs font-medium text-slate-600 uppercase">
                          <th className="px-3 py-2">Request</th>
                          <th className="px-3 py-2">Role</th>
                          {(overdueMode === 'pending' || overdueMode === 'mine') && (
                            <th className="px-3 py-2">Next step</th>
                          )}
                          {(overdueMode === 'all' || overdueMode === 'all-pending') && (
                            <th className="px-3 py-2">Assignee</th>
                          )}
                          <th className="px-3 py-2">Due</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filtered.map(item => (
                          <tr key={item.assignment_id} className="hover:bg-slate-50" data-testid={`overdue-row-${item.assignment_id}`}>
                            <td className="px-3 py-2 align-top max-w-[20rem]">
                              <button
                                onClick={() => { setShowOverdueModal(false); navigate(`/requests/${item.request_id}`); }}
                                className="text-blue-600 hover:underline font-mono text-xs"
                                data-testid={`overdue-open-${item.assignment_id}`}
                              >
                                {item.ref_no}
                              </button>
                              <p className="text-xs text-slate-600 truncate" title={item.request_title}>
                                {item.request_title || '—'}
                              </p>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Badge variant="outline" className="text-xs capitalize">
                                {(item.assignment_role || '').toLowerCase().replace(/_/g, ' ')}
                              </Badge>
                            </td>
                            {(overdueMode === 'pending' || overdueMode === 'mine') && (
                              <td className="px-3 py-2 align-top max-w-[16rem]">
                                <p className="text-xs font-medium text-slate-800">
                                  {item.action_label || 'Take action'}
                                </p>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  Status: {(item.request_status || '').replace(/_/g, ' ')}
                                </p>
                              </td>
                            )}
                            {(overdueMode === 'all' || overdueMode === 'all-pending') && (
                              <td className="px-3 py-2 align-top max-w-[16rem]">
                                <p className="font-medium text-slate-800 truncate">{item.assignee_name}</p>
                                {item.assignee_email && (
                                  <p className="text-xs text-slate-500 truncate">{item.assignee_email}</p>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 align-top">
                              <p className="text-xs text-slate-700">{item.due_date ? formatDate(item.due_date) : 'No due date'}</p>
                              {item.is_overdue ? (
                                <p className="text-xs font-medium text-red-600">
                                  {item.days_overdue}d overdue
                                </p>
                              ) : item.due_date ? (
                                <p className="text-xs text-slate-500">On track</p>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 align-top text-right">
                              {(overdueMode === 'mine' || overdueMode === 'pending' || overdueMode === 'all-pending') ? (
                                <Button
                                  size="sm"
                                  onClick={() => { setShowOverdueModal(false); navigate(`/requests/${item.request_id}`); }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid={`overdue-goto-${item.assignment_id}`}
                                >
                                  Open <ArrowRight className="w-3 h-3 ml-1" />
                                </Button>
                              ) : item.last_reminder_sent_at && !item.can_send_reminder ? (
                                <div className="inline-flex flex-col items-end gap-0.5">
                                  <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 bg-amber-50 text-amber-700">
                                    <Clock className="w-3 h-3" /> Cooldown
                                  </Badge>
                                  <span className="text-[10px] text-slate-500">
                                    ~{item.reminder_cooldown_hours}h
                                  </span>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSendSingleReminder(item.assignment_id)}
                                  disabled={sendingReminderFor === item.assignment_id || !item.can_send_reminder}
                                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`overdue-send-reminder-${item.assignment_id}`}
                                  title={!item.assignee_email ? 'Assignee has no email on file' : 'Send urgent reminder email'}
                                >
                                  {sendingReminderFor === item.assignment_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <><Mail className="w-3 h-3 mr-1" /> Remind</>
                                  )}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>

          {overdueMode === 'all' && (
            <p className="text-xs text-slate-500 mt-2 px-1">
              Urgent reminder emails are rate-limited to 1 per assignment per 24h.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
